import { patchHTML } from "../core/surface-host.js";
import { ensureAdminContentLayout } from "./layout.js";
import { escapeHTML, renderSkeleton, timeAgo } from "../render/ui-helpers.js";
import { renderHarnessThreadCreateFields } from "./thread-create/harness.js";
import { renderLLMThreadCreateFields } from "./thread-create/llm.js";

export function renderThreadsRoute(layoutRoot, state, options = {}) {
  ensureAdminContentLayout(layoutRoot, "threads", `
    <div class="threads-page">
      <div data-threads-header-root></div>
      <div data-threads-create-root></div>
      <div data-threads-list-root></div>
    </div>
  `);
  const headerRoot = layoutRoot.querySelector("[data-threads-header-root]");
  const createRoot = layoutRoot.querySelector("[data-threads-create-root]");
  const listRoot = layoutRoot.querySelector("[data-threads-list-root]");
  if (!state.apiKey) {
    patchHTML(headerRoot, "");
    patchHTML(createRoot, "");
    patchHTML(listRoot, options.renderLockedState?.() || "");
    return;
  }
  if (state.loading) {
    patchHTML(headerRoot, "");
    patchHTML(createRoot, "");
    patchHTML(listRoot, renderSkeleton(4));
    return;
  }
  const pageState = buildThreadsPageState(state);
  patchHTML(headerRoot, renderThreadsHeaderSection(pageState));
  patchHTML(createRoot, renderThreadsCreateSection(state, pageState));
  patchHTML(listRoot, renderThreadsListSection(state, pageState, options));
}

export function buildThreadsPageState(state) {
  const candidates = state.agents;
  const threadDraft = state.forms.thread;
  const selectedAgent = candidates.find((item) => item.id === threadDraft.agent_id) || null;
  const selectedAgentMode = String(selectedAgent?.execution_mode || "harness");
  const needsInputIDs = new Set((state.pendingDecisions || []).map((item) => item.thread_id).filter(Boolean));
  const activeStates = new Set(["open", "running", "merging", "awaiting_remote_merge", "recovery_required", "merge_conflicted"]);
  const readyStates = new Set(["ready_to_merge"]);
  const counts = {
    all: state.threads.length,
    active: state.threads.filter((item) => activeStates.has(item.state)).length,
    ready: state.threads.filter((item) => readyStates.has(item.state)).length,
    input: needsInputIDs.size,
    archived: state.threads.filter((item) => item.state === "archived").length
  };
  const filter = state.threadFilter;
  const filtered = state.threads.filter((item) => {
    if (filter === "active") return activeStates.has(item.state);
    if (filter === "ready") return readyStates.has(item.state);
    if (filter === "input") return needsInputIDs.has(item.id);
    if (filter === "archived") return item.state === "archived";
    return true;
  });
  const hasLLMCandidate = candidates.some((item) => String(item.execution_mode || "harness") === "llm");
  const canCreate = candidates.length > 0 && (state.workspaces.length > 0 || hasLLMCandidate);
  const showCreate = state.threadCreateOpen && canCreate;
  const filters = [
    { id: "all", label: "All", count: counts.all },
    { id: "active", label: "Active", count: counts.active },
    { id: "ready", label: "Ready", count: counts.ready },
    { id: "input", label: "Needs input", count: counts.input },
    { id: "archived", label: "Archived", count: counts.archived }
  ];
  return { candidates, threadDraft, selectedAgent, selectedAgentMode, needsInputIDs, filter, filtered, canCreate, showCreate, filters };
}

export function renderThreadsHeaderSection(pageState) {
  return `
    <div class="threads-header">
      <div class="threads-filter-bar" role="tablist">
        ${pageState.filters.map((item) => `
          <button class="threads-filter__item ${item.id === pageState.filter ? "threads-filter__item--active" : ""} ${item.id === "input" && item.count > 0 ? "threads-filter__item--attention" : ""}" data-thread-filter="${escapeHTML(item.id)}">
            ${escapeHTML(item.label)} <span class="threads-filter__count">${item.count}</span>
          </button>
        `).join("")}
      </div>
      <button class="button button--sm ${pageState.showCreate ? "button--ghost" : "button--primary"}" type="button" data-action="toggle-thread-create">${pageState.showCreate ? "Cancel" : "+ New"}</button>
    </div>
  `;
}

export function renderThreadsCreateSection(state, pageState) {
  if (!pageState.showCreate) {
    return "";
  }
  return `
    <div class="threads-create-inline">
      ${pageState.candidates.length === 0 ? `
        <div class="form-note form-note--warn">Create an agent first. <a class="button button--sm button--primary" href="/agents" data-route style="margin-left:var(--space-sm)">Agents</a></div>
      ` : `
        <form id="thread-form" class="threads-create-form">
          <select id="thread-agent-id" name="agent_id" required>
            <option value="">Agent</option>
            ${pageState.candidates.map((item) => `<option value="${escapeHTML(item.id)}" ${pageState.threadDraft.agent_id === item.id ? "selected" : ""}>${escapeHTML(item.name)} (${escapeHTML(item.execution_mode || "harness")})</option>`).join("")}
          </select>
          ${renderThreadCreateModeSection(state, pageState)}
          <button class="button button--primary" type="submit">Create</button>
        </form>
        ${state.formErrors.thread ? `<div class="form-error" style="margin-top:var(--space-sm)">${escapeHTML(state.formErrors.thread)}</div>` : ""}
      `}
    </div>
  `;
}

function renderThreadCreateModeSection(state, pageState) {
  if (!pageState.selectedAgent) {
    return `<div class="form-note">Select an agent to choose the thread mode and required fields.</div>`;
  }
  if (pageState.selectedAgentMode === "llm") {
    return renderLLMThreadCreateFields(state, pageState);
  }
  return renderHarnessThreadCreateFields(state, pageState);
}

export function renderThreadsListSection(state, pageState, options = {}) {
  return `
    <div class="threads-list">
      ${pageState.filtered.length ? pageState.filtered.map((item) => renderThreadRow(state, item, pageState.needsInputIDs.has(item.id), options)).join("") : `
        <div class="threads-empty">
          ${state.threads.length === 0 ? `
            <div class="threads-empty__title">No threads yet</div>
            <div class="threads-empty__copy">Create your first thread to start an agent conversation.</div>
            ${pageState.canCreate ? `<button class="button button--primary" type="button" data-action="toggle-thread-create">+ New thread</button>` : `<a class="button button--primary" href="/agents" data-route>Set up agents first</a>`}
          ` : `
            <div class="threads-empty__title">No matches</div>
            <div class="threads-empty__copy">No threads match the current filter.</div>
          `}
        </div>
      `}
    </div>
  `;
}

export function renderThreadRow(state, item, needsInput, options = {}) {
  const selected = state.route.id === "thread-detail" && state.route.params.threadID === item.id;
  const borderTone = needsInput ? "input" : item.state === "running" ? "running" : item.state === "ready_to_merge" ? "ready" : item.state === "recovery_required" || item.state === "merge_conflicted" ? "danger" : item.state === "archived" ? "archived" : "default";
  const ago = timeAgo(item.updated_at);
  const title = typeof options.threadTitle === "function" ? options.threadTitle(item) : item.id;
  const preview = typeof options.threadPreviewText === "function" ? options.threadPreviewText(item) : "";
  return `
    <a class="thread-row thread-row--border-${escapeHTML(borderTone)} ${selected ? "thread-row--selected" : ""}" href="/threads/${encodeURIComponent(item.id)}" data-route>
      <div class="thread-row__main">
        <div class="thread-row__body">
          <div class="thread-row__title">${escapeHTML(title)}</div>
          <div class="thread-row__meta">${escapeHTML(preview)}</div>
          ${item.draft_branch ? `<div class="thread-row__branch">${escapeHTML(item.draft_branch)}</div>` : ""}
        </div>
        <div class="thread-row__side">
          <span class="badge badge--${escapeHTML(item.mode || "harness")}">${escapeHTML((item.mode || "harness").toUpperCase())}</span>
          <span class="badge badge--${escapeHTML(item.state)}">${escapeHTML(item.state.replaceAll("_", " "))}</span>
          ${ago ? `<span class="thread-row__time">${escapeHTML(ago)}</span>` : ""}
        </div>
      </div>
    </a>
  `;
}
